import React, { useState, useRef } from 'react';
import { Camera, Upload, RotateCcw, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';

const PhotoCapture = ({ onPhotoSubmit, onSkip }) => {
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { width: 400, height: 400, facingMode: 'user' } 
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setShowCamera(true);
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Unable to access camera. Please use file upload instead.');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    
    if (canvas && video) {
      const context = canvas.getContext('2d');
      canvas.width = 400;
      canvas.height = 400;
      
      // Draw the video frame to the canvas
      context.drawImage(video, 0, 0, 400, 400);
      
      // Convert to data URL
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedPhoto(dataUrl);
      stopCamera();
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCapturedPhoto(e.target.result);
      };
      reader.readAsDataURL(file);
    }
    
    // Reset the input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const retakePhoto = () => {
    setCapturedPhoto(null);
    setShowCamera(false);
  };

  const submitPhoto = () => {
    if (capturedPhoto) {
      onPhotoSubmit(capturedPhoto);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-red-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <Camera className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h1 className="text-3xl font-bold bg-gradient-to-r from-red-500 to-pink-500 bg-clip-text text-transparent mb-2">
            Add Your Photo
          </h1>
          <p className="text-gray-600">
            Upload a great photo to complete your profile and start matching!
          </p>
        </div>

        {!capturedPhoto && !showCamera && (
          <div className="space-y-4">
            {/* Camera Option */}
            <Button
              onClick={startCamera}
              className="w-full bg-gradient-to-r from-red-500 to-pink-500 text-white py-3"
            >
              <Camera className="w-5 h-5 mr-2" />
              Take Photo with Camera
            </Button>

            {/* Upload Option */}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                className="w-full py-3"
              >
                <Upload className="w-5 h-5 mr-2" />
                Upload from Device
              </Button>
            </div>

            {/* Skip Option */}
            <Button
              onClick={onSkip}
              variant="ghost"
              className="w-full text-gray-600"
            >
              Skip for now
            </Button>
          </div>
        )}

        {showCamera && (
          <div className="space-y-4">
            <div className="relative bg-black rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-80 object-cover"
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={stopCamera}
                variant="outline"
                className="flex-1"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={capturePhoto}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              >
                <Camera className="w-4 h-4 mr-2" />
                Capture
              </Button>
            </div>
          </div>
        )}

        {capturedPhoto && (
          <div className="space-y-4">
            <div className="text-center">
              <div className="relative inline-block">
                <img
                  src={capturedPhoto}
                  alt="Captured"
                  className="w-64 h-64 object-cover rounded-lg border-4 border-white shadow-lg"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={retakePhoto}
                variant="outline"
                className="flex-1"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Retake
              </Button>
              <Button
                onClick={submitPhoto}
                className="flex-1 bg-gradient-to-r from-red-500 to-pink-500 text-white"
              >
                <Check className="w-4 h-4 mr-2" />
                Looks Good!
              </Button>
            </div>
          </div>
        )}

        <canvas ref={canvasRef} className="hidden" />

        {/* Photo Tips */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-2">Photo Tips:</p>
            <ul className="space-y-1 list-disc list-inside text-xs">
              <li>Make sure you're well-lit and clearly visible</li>
              <li>Smile and show your personality</li>
              <li>Avoid sunglasses or hats that hide your face</li>
              <li>You can always add more photos later in your profile</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PhotoCapture;